import React, { useState, useEffect, useCallback, useRef } from "react";
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Alert,
  Spinner
} from "react-bootstrap";
import { useTranslation } from 'react-i18next';
import Layout from "../components/Layout";
import { useAuth } from "../hooks/useAuth";
import { dashboardAPI } from "../utils/api";
import type { AttendanceRecord, DashboardResponse } from "../types/dashboard";
import ClassroomSummaryCard from "../components/ClassroomSummaryCard";
import AttendanceChartWidget from "../components/AttendanceChartWidget";
import DetailList from "../components/DetailList";
import { formatDateInNurseryTimezone, getTodayDateInNurseryTimezone } from "../utils/timezone";

import ScopeIndicator from '../components/ScopeIndicator';

const Dashboard: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'dashboard' });
  const { currentNursery, nurseries, nurseriesLoading } = useAuth();
  
  // Get nursery timezone with fallback
  const nurseryTimezone = currentNursery?.timezone || 'UTC';
  
  const [dashboardData, setDashboardData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [childrenExpanded, setChildrenExpanded] = useState(false);

  const prevNurseryIdRef = useRef<string | undefined>(undefined);
  const lastFetchedNurseryIdRef = useRef<string | undefined>(undefined);
  const lastFetchedDateRef = useRef<string>('');

  // Load dashboard data (used when user changes date only)
  const loadDashboardData = useCallback(async () => {
    if (!currentNursery?.nursery_id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await dashboardAPI.getDashboardData(currentNursery.nursery_id, { date: selectedDate });
      setDashboardData(response);
    } catch (err: any) {
      console.error('Failed to load dashboard data:', err);
      setError(err.response?.data?.error || t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentNursery?.nursery_id, selectedDate, t]);

  // Load data on mount, nursery change, or date change. On nursery change: clear stale data, set date to new nursery's today, then fetch (avoids race with selectedDate).
  useEffect(() => {
    const nurseryId = currentNursery?.nursery_id;
    if (!nurseryId || !currentNursery?.timezone) return;

    const nurseryChanged = prevNurseryIdRef.current !== nurseryId;
    if (nurseryChanged) {
      prevNurseryIdRef.current = nurseryId;
      setDashboardData(null);
      const newDate = getTodayDateInNurseryTimezone(currentNursery.timezone);
      setSelectedDate(newDate);
      setLoading(true);
      setError(null);
      dashboardAPI
        .getDashboardData(nurseryId, { date: newDate })
        .then(setDashboardData)
        .catch((err: any) => {
          console.error('Failed to load dashboard data:', err);
          setError(err.response?.data?.error || t('loadFailed'));
        })
        .finally(() => setLoading(false));
      lastFetchedNurseryIdRef.current = nurseryId;
      lastFetchedDateRef.current = newDate;
      return;
    }

    if (lastFetchedNurseryIdRef.current === nurseryId && lastFetchedDateRef.current === selectedDate) return;
    lastFetchedNurseryIdRef.current = nurseryId;
    lastFetchedDateRef.current = selectedDate;
    loadDashboardData();
  }, [currentNursery?.nursery_id, currentNursery?.timezone, selectedDate, loadDashboardData]);

  // Handle date change
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
  };

  // Format date for display using nursery timezone
  const formatDate = (dateString: string) => {
    if (!currentNursery?.timezone) return 'N/A';
    return formatDateInNurseryTimezone(dateString, currentNursery.timezone);
  };

  const getHalfDayRecords = (data: DashboardResponse): AttendanceRecord[] => {
    if (Array.isArray(data.HalfDayList)) return data.HalfDayList;

    return data.PresentList.filter((record) => {
      const status = String(record.status || '').toLowerCase();
      const dropoff = record.dropoff_time || record.check_in;
      const pickup = record.pickup_time || record.check_out;
      return status === 'half day' || (!!dropoff && !!pickup && dropoff === pickup);
    });
  };

  const getNurseryMetrics = (data: DashboardResponse) => {
    const halfDayRecords = getHalfDayRecords(data);
    const halfDayCount = data.totalHalfDay ?? halfDayRecords.length;
    const presentCount = Math.max(0, data.total_present - halfDayCount);

    return {
      halfDayRecords,
      halfDayCount,
      presentCount,
      cards: [
        {
          label: t('totalChildren'),
          value: data.totalChildrenCount,
          hint: t('registeredChildren', { defaultValue: 'Enrolled children' }),
          gradient: 'linear-gradient(135deg, #27b6c8 0%, #1a8a99 100%)',
          shadow: 'rgba(39, 182, 200, 0.3)',
          iconClass: 'bi-people-fill',
        },
        {
          label: t('present', { defaultValue: 'Present' }),
          value: presentCount,
          hint: t('presentHint', { defaultValue: 'Dropped off and active today' }),
          gradient: 'linear-gradient(135deg, #2fb344 0%, #1b7f35 100%)',
          shadow: 'rgba(47, 179, 68, 0.3)',
          iconClass: 'bi-check-circle-fill',
        },
        {
          label: t('absent', { defaultValue: 'Absent' }),
          value: data.total_absent,
          hint: t('absentHint', { defaultValue: 'No drop-off detected' }),
          gradient: 'linear-gradient(135deg, #dc3545 0%, #a71d2a 100%)',
          shadow: 'rgba(220, 53, 69, 0.3)',
          iconClass: 'bi-x-circle-fill',
        },
        {
          label: t('halfDay', { defaultValue: 'Half Day' }),
          value: halfDayCount,
          hint: t('halfDayHint', { defaultValue: 'Partial day attendance' }),
          gradient: 'linear-gradient(135deg, #ffc107 0%, #d39e00 100%)',
          shadow: 'rgba(255, 193, 7, 0.3)',
          iconClass: 'bi-clock-history',
        },
      ],
    };
  };

  if (nurseriesLoading) {
    return (
      <Layout title={t('title')}>
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
            <p className="text-muted">{t('loading')}</p>
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={t('title')}>
      <div className="dashboard-container" style={{ 
        minHeight: '100vh',
        padding: '2rem 0'
      }}>
        <Container fluid className="px-4">
          {/* Modern Header Section */}
          <Row className="mb-4">
            <Col>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="fw-bold ll-page-title mb-2" style={{ fontSize: '2.5rem', letterSpacing: '-0.5px' }}>
                    {t('title')}
                  </h2>
                  <p className="ll-page-subtitle mb-0" style={{ fontSize: '1.1rem' }}>
                    {t('subtitle')}
                  </p>
                </div>
                
                <div className="d-flex align-items-center gap-3">
                  <div className="position-relative">
                    <input
                      type="date"
                      className="form-control"
                      value={selectedDate}
                      onChange={handleDateChange}
                      max={getTodayDateInNurseryTimezone(nurseryTimezone)}
                      style={{ 
                        minWidth: '180px',
                        border: '2px solid rgba(15,23,42,0.08)',
                        borderRadius: '12px',
                        padding: '0.75rem 1rem',
                        fontSize: '1rem',
                        boxShadow: '0 4px 20px rgba(15,23,42,0.08)',
                        transition: 'all 0.3s ease'
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#49B9D9';
                        e.target.style.boxShadow = '0 4px 16px rgba(73, 185, 217, 0.2)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'rgba(15,23,42,0.08)';
                        e.target.style.boxShadow = '0 4px 20px rgba(15,23,42,0.08)';
                      }}
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <ScopeIndicator resource="dashboard" />

          {/* No Nursery Alert */}
          {!currentNursery && nurseries.length === 0 && (
            <Row className="mb-4">
              <Col xs={12}>
                <Alert variant="info" className="border-0" style={{
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%)',
                  border: '1px solid #bee5eb',
                  boxShadow: '0 4px 20px rgba(15,23,42,0.08)'
                }}>
                  <Alert.Heading className="fw-bold">{t('noNurseryTitle')}</Alert.Heading>
                  <p className="mb-0">
                    {t('noNurseryBody')}
                  </p>
                </Alert>
              </Col>
            </Row>
          )}

          {/* Loading State */}
          {loading && (
            <Row className="justify-content-center mb-4">
              <Col xs={12} className="text-center">
                <div className="p-5">
                  <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                  <p className="text-muted fs-5">{t('loadingData')}</p>
                </div>
              </Col>
            </Row>
          )}

          {/* Error State */}
          {error && (
            <Row className="mb-4">
              <Col xs={12}>
                <Alert variant="danger" onClose={() => setError(null)} dismissible className="border-0" style={{
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)',
                  border: '1px solid #f5c6cb',
                  boxShadow: '0 4px 20px rgba(15,23,42,0.08)'
                }}>
                  <Alert.Heading className="fw-bold">{t('errorTitle')}</Alert.Heading>
                  <p className="mb-0">{error}</p>
                </Alert>
              </Col>
            </Row>
          )}

          {/* Dashboard Content */}
          {!loading && dashboardData && (
            <>
              {dashboardData.is_holiday && (
                <Row className="mb-4">
                  <Col xs={12}>
                    <Alert variant="info" className="border-0" style={{
                      borderRadius: '16px',
                      background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
                      boxShadow: '0 4px 20px rgba(15,23,42,0.08)'
                    }}>
                      <Alert.Heading className="fw-bold">{t('holidayTitle')}</Alert.Heading>
                      <p className="mb-0">{t('holidayBody')}</p>
                    </Alert>
                  </Col>
                </Row>
              )}

              {/* Nursery Attendance Stats */}
              <Row className="mb-4">
                {getNurseryMetrics(dashboardData).cards.map((metric) => (
                  <Col xs={12} sm={6} xl={3} key={metric.label} className="mb-3">
                    <Card className="border-0 h-100" style={{
                      borderRadius: '20px',
                      background: metric.gradient,
                      boxShadow: `0 8px 32px ${metric.shadow}`,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = `0 16px 48px ${metric.shadow}`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 8px 32px ${metric.shadow}`;
                    }}>
                      <Card.Body className="p-4 text-white">
                        <div className="d-flex align-items-center justify-content-between gap-3">
                          <div>
                            <h6 className="text-white-50 mb-2" style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                              {metric.label}
                            </h6>
                            <h2 className="mb-0 fw-bold" style={{ fontSize: '2.5rem' }}>
                              {metric.value}
                            </h2>
                            <p className="mb-0 text-white-50" style={{ fontSize: '0.9rem' }}>
                              {metric.hint}
                            </p>
                          </div>
                          <i className={`bi ${metric.iconClass}`} style={{ fontSize: '2.8rem', opacity: '0.8' }}></i>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* Child Details */}
              <Row className="mb-4">
                <Col xs={12}>
                  <div>
                    {/* Total Children Card */}
                    <Card className="border-0" style={{
                      borderRadius: '20px',
                      background: 'linear-gradient(135deg, #27b6c8 0%, #1a8a99 100%)',
                      boxShadow: '0 8px 32px rgba(39, 182, 200, 0.3)',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 16px 48px rgba(39, 182, 200, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 32px rgba(39, 182, 200, 0.3)';
                    }}>
                      <Card.Body className="p-4 text-white">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <h6 className="text-white-50 mb-2" style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                              {t('totalChildren')}
                            </h6>
                            <h2 className="mb-0 fw-bold" style={{ fontSize: '2.5rem' }}>
                              {dashboardData.totalChildrenCount}
                            </h2>
                            <p className="mb-0 text-white-50" style={{ fontSize: '0.9rem' }}>
                              {t('registeredChildren', { defaultValue: 'Enrolled children' })}
                            </p>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-people-fill" style={{ fontSize: '2.8rem', opacity: 0.9 }} aria-hidden="true"></i>
                          <button
                              onClick={() => setChildrenExpanded(!childrenExpanded)}
                              className="btn btn-link text-white p-0 border-0"
                              style={{ fontSize: '1.2rem', textDecoration: 'none' }}
                              aria-expanded={childrenExpanded}
                            >
                              {childrenExpanded ? (
                                <i className="bi bi-chevron-up"></i>
                              ) : (
                                <i className="bi bi-chevron-down"></i>
                              )}
                            </button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>

                    {/* Child Details - Displayed under Total Children */}
                    {childrenExpanded && (
                      <Card className="border-0" style={{
                        borderRadius: '16px',
                        background: '#ffffff',
                        boxShadow: '0 4px 20px rgba(15,23,42,0.08)',
                        border: '1px solid rgba(15,23,42,0.08)'
                      }}>
                        <Card.Body className="p-4">
                          <div className="d-flex align-items-center mb-3">
                            <div style={{
                              width: '3px',
                              height: '30px',
                              background: 'linear-gradient(135deg, #27b6c8 0%, #1a8a99 100%)',
                              borderRadius: '2px',
                              marginInlineEnd: '1rem'
                            }}></div>
                            <h6 className="mb-0 fw-bold text-dark">{t('childDetails')}</h6>
                          </div>
                          <DetailList 
                            type="children" 
                            data={dashboardData.totalChildrenList}
                            nurseryTimezone={nurseryTimezone}
                          />
                        </Card.Body>
                      </Card>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Attendance Overview - Enhanced Section */}
              <Row className="mb-4">
                <Col xs={12}>
                  <Card className="border-0" style={{
                    borderRadius: '20px',
                    background: '#ffffff',
                    boxShadow: '0 4px 20px rgba(15,23,42,0.08)',
                    border: '1px solid rgba(15,23,42,0.08)'
                  }}>
                    <Card.Body className="p-0">
                      <AttendanceChartWidget
                        title={t('attendanceOverview', { date: formatDate(selectedDate) })}
                        presentCount={getNurseryMetrics(dashboardData).presentCount}
                        halfDayCount={getNurseryMetrics(dashboardData).halfDayCount}
                        absentCount={dashboardData.total_absent}
                        vacationCount={dashboardData.totalVacation}
                        holidayCount={dashboardData.totalHoliday || 0}
                        totalCount={dashboardData.totalChildrenCount}
                        presentData={dashboardData.PresentList}
                        halfDayData={getNurseryMetrics(dashboardData).halfDayRecords}
                        absentData={dashboardData.AbsentList}
                        vacationData={dashboardData.VacationList}
                        holidayData={dashboardData.HolidayList || []}
                        nurseryTimezone={nurseryTimezone}
                      />
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Classroom Breakdown - Modern Cards */}
              {dashboardData.classroomsSummary && dashboardData.classroomsSummary.length > 0 && (
                <Row className="mb-4">
                  <Col xs={12}>
                    <Card className="border-0" style={{
                      borderRadius: '20px',
                      background: '#ffffff',
                      boxShadow: '0 4px 20px rgba(15,23,42,0.08)',
                      border: '1px solid rgba(15,23,42,0.08)'
                    }}>
                      <Card.Body className="p-4">
                        <div className="d-flex align-items-center mb-4">
                          <div style={{
                            width: '4px',
                            height: '40px',
                            background: '#49B9D9',
                            borderRadius: '2px',
                            marginInlineEnd: '1rem'
                          }}></div>
                          <h4 className="mb-0 fw-bold text-primary">
                            {t('classroomPerformance')}
                          </h4>
                        </div>
                        
                        <Row>
                          {dashboardData.classroomsSummary.map((classroom, index) => (
                            <Col xs={12} key={index} className="mb-3">
                              <ClassroomSummaryCard classroom={classroom} nurseryTimezone={nurseryTimezone} />
                            </Col>
                          ))}
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* No Classroom Data Message */}
              {(!dashboardData.classroomsSummary || dashboardData.classroomsSummary.length === 0) && (
                <Row className="mb-4">
                  <Col xs={12}>
                    <Card className="text-center border-0" style={{
                      borderRadius: '20px',
                      background: '#F8FAFC',
                      boxShadow: '0 4px 20px rgba(15,23,42,0.08)'
                    }}>
                      <Card.Body className="py-5">
                        <div style={{ fontSize: '4rem', opacity: '0.3', marginBottom: '1rem' }}>📊</div>
                        <h5 className="mb-2 fw-bold text-muted">{t('noClassroomDataTitle')}</h5>
                        <p className="text-muted mb-0">
                          {t('noClassroomDataBody')}
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}
            </>
          )}

          {/* No Data State */}
          {!loading && !dashboardData && !error && (
            <Row className="mb-4">
              <Col xs={12}>
                <Card className="text-center border-0" style={{
                  borderRadius: '20px',
                  background: '#F8FAFC',
                  boxShadow: '0 4px 20px rgba(15,23,42,0.08)'
                }}>
                  <Card.Body className="py-5">
                    <div style={{ fontSize: '4rem', opacity: '0.3', marginBottom: '1rem' }}>📈</div>
                    <h5 className="mb-2 fw-bold text-muted">{t('noDashboardDataTitle')}</h5>
                    <p className="text-muted mb-0">
                      {t('noDashboardDataBody')}
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </Layout>
  );
};

export default Dashboard;
